import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { renderMathContent, renderAnswerChoice } from '../components/amcMathRendering';
import 'katex/dist/katex.min.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const CHOICE_LETTERS = ['A', 'B', 'C', 'D', 'E'] as const;

interface SampleProblem {
  id: string;
  problemNumber: number;
  problemStatement: string;
  answerChoices: Partial<Record<(typeof CHOICE_LETTERS)[number], string>> | null;
  problemImage?: string | null;
}

interface SampleContest {
  id: string;
  name: string;
  year: number;
  contestType: string;
  version: string;
  totalProblems: number;
}

const SampleCTA: React.FC = () => {
  const { user, isLoading } = useUser();

  if (isLoading) return null;

  if (user) {
    return (
      <div className="amc-sample-cta">
        <Link className="btn btn-primary" to="/amc">
          Go to your practice dashboard →
        </Link>
      </div>
    );
  }

  return (
    <div className="amc-sample-cta">
      <Link className="btn btn-primary" to="/login">
        Sign in to take it scored
      </Link>
      <Link className="btn btn-secondary" to="/family-signup">
        Create a family account
      </Link>
    </div>
  );
};

const AMCSamplePage: React.FC = () => {
  const [contest, setContest] = useState<SampleContest | null>(null);
  const [problems, setProblems] = useState<SampleProblem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/amc/sample`)
      .then((response) => {
        if (cancelled) return;
        setContest(response.data.contest || null);
        setProblems(response.data.problems || []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setContest(null);
          setProblems([]);
        } else {
          setError('Could not load the sample contest. Please try again in a moment.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            AMC practice · free sample
          </span>
          <h1 style={{ maxWidth: '24ch' }}>
            {contest ? `${contest.name} — the full problem set.` : 'A real AMC contest, in full.'}
          </h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            Every problem from a real past contest, free to read — no account needed. Sign in to
            take it under the official 75-minute clock with scoring and per-topic analytics on
            every miss.
          </p>
          <div style={{ marginTop: 24 }}>
            <SampleCTA />
          </div>
        </div>
      </header>

      {/* Problems */}
      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap" style={{ maxWidth: 880 }}>
          {loading && (
            <p className="meta" style={{ textAlign: 'center', padding: '40px 0' }}>
              Loading the sample contest…
            </p>
          )}

          {!loading && error && (
            <div className="note" style={{ textAlign: 'center' }}>{error}</div>
          )}

          {!loading && !error && problems.length === 0 && (
            <div className="note" style={{ textAlign: 'center' }}>
              No sample contest is available right now — check back soon, or{' '}
              <Link to="/family-signup" style={{ textDecoration: 'underline' }}>
                create a free account
              </Link>{' '}
              to browse the full practice library.
            </div>
          )}

          {!loading &&
            !error &&
            problems.map((problem) => (
              <div key={problem.id} className="card amc-sample-card">
                <div className="meta" style={{ marginBottom: 10 }}>
                  Problem {problem.problemNumber}
                </div>
                <div className="amc-sample-statement">
                  {renderMathContent(problem.problemStatement)}
                </div>
                {problem.problemImage && (
                  <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <img
                      src={problem.problemImage}
                      alt={`Problem ${problem.problemNumber} diagram`}
                      style={{ maxWidth: '100%', height: 'auto', display: 'inline-block' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {problem.answerChoices && (
                  <div className="amc-sample-choices">
                    {CHOICE_LETTERS.map((letter) => {
                      const value = problem.answerChoices?.[letter];
                      if (!value) return null;
                      return (
                        <span key={letter}>
                          <b style={{ fontWeight: 600 }}>({letter})</b>{' '}
                          {renderAnswerChoice(value)}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

          {!loading && !error && problems.length > 0 && (
            <div style={{ marginTop: 40, textAlign: 'center' }}>
              <p style={{ color: 'var(--ink-secondary)', marginBottom: 4 }}>
                Want the clock, the score, and analytics on every miss?
              </p>
              <SampleCTA />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default AMCSamplePage;
